// Landing Page Header Component
// ==============================

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Button from '../ui/Button';
import { LyraLogo } from '../ui';

const LandingHeader: React.FC = () => {
  const { t, i18n } = useTranslation('landing');
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  const toggleLanguage = () => {
    const newLang = i18n.language.startsWith('da') ? 'en' : 'da';
    i18n.changeLanguage(newLang);
    localStorage.setItem('lyra-language', newLang);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo - Feature #155: SVG logo */}
          <Link to="/" className="flex items-center space-x-2">
            <LyraLogo size="md" showText={true} />
            <span className="px-2 py-0.5 text-xs bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-200 rounded-full font-medium">
              {t('header.beta')}
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <button
              onClick={() => scrollToSection('features')}
              className="text-neutral-dark dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
            >
              {t('header.features')}
            </button>
            <button
              onClick={() => scrollToSection('how-it-works')}
              className="text-neutral-dark dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
            >
              {t('header.howItWorks')}
            </button>
            <button
              onClick={() => scrollToSection('beta')}
              className="text-neutral-dark dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
            >
              {t('header.betaVersion')}
            </button>
          </div>

          {/* Desktop CTA Buttons */}
          <div className="hidden md:flex items-center space-x-4">
            <button
              onClick={toggleLanguage}
              className="text-sm font-medium text-neutral-dark dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
            >
              {i18n.language.startsWith('da') ? 'EN' : 'DA'}
            </button>
            <Link to="/login">
              <Button variant="outline" size="sm">
                {t('header.logIn')}
              </Button>
            </Link>
            <Link to="/register">
              <Button variant="primary" size="sm">
                {t('header.getStarted')}
              </Button>
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden p-2 rounded-lg text-neutral-dark dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={t('header.toggleMenu')}
          >
            {isMobileMenuOpen ? (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
            <div className="flex flex-col space-y-4">
              <button
                onClick={() => scrollToSection('features')}
                className="text-left px-4 py-2 text-neutral-dark dark:text-gray-300 hover:text-primary-600"
              >
                {t('header.features')}
              </button>
              <button
                onClick={() => scrollToSection('how-it-works')}
                className="text-left px-4 py-2 text-neutral-dark dark:text-gray-300 hover:text-primary-600"
              >
                {t('header.howItWorks')}
              </button>
              <button
                onClick={() => scrollToSection('beta')}
                className="text-left px-4 py-2 text-neutral-dark dark:text-gray-300 hover:text-primary-600"
              >
                {t('header.betaVersion')}
              </button>
              <button
                onClick={toggleLanguage}
                className="text-left px-4 py-2 text-sm font-medium text-neutral-dark dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
              >
                {i18n.language.startsWith('da') ? 'EN' : 'DA'}
              </button>
              <div className="flex flex-col space-y-2 px-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <Link to="/login">
                  <Button variant="outline" fullWidth>
                    {t('header.logIn')}
                  </Button>
                </Link>
                <Link to="/register">
                  <Button variant="primary" fullWidth>
                    {t('header.getStarted')}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default LandingHeader;
